import * as React from "react";

import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

const PREFIX_LENGTH = 3;
const CODE_LENGTH = 6;
const HINT = "Letters and numbers, 6 characters";

/** Ward short-code prefix: a human-chosen code (e.g. `ONC`), so any letter or
 *  digit is allowed here — including O/I/L, which the alphabet below bans. */
const PREFIX_CHAR = /[A-Z0-9]/;

/**
 * The unambiguous alphabet from the v1 design (§6.6) — no O/0, I/1 or L, so the
 * 3-char random suffix can't be misread off a whiteboard or misheard on a call.
 * It only constrains the suffix; the ward prefix is unrestricted. A banned char
 * typed or pasted into a suffix position is dropped silently.
 */
const SUFFIX_CHAR = /[ABCDEFGHJKMNPQRSTUVWXYZ23456789]/;

/**
 * Reduce arbitrary input (typed, pasted, lowercase, hyphenated) down to the
 * canonical unformatted code: uppercased, non-alphanumerics stripped, the first
 * three chars kept as the ward prefix and the rest restricted to the suffix
 * alphabet, capped at six characters.
 */
function sanitize(input: string): string {
  let result = "";
  for (const char of input.toUpperCase()) {
    if (result.length >= CODE_LENGTH) break;
    const allowed = result.length < PREFIX_LENGTH ? PREFIX_CHAR : SUFFIX_CHAR;
    if (allowed.test(char)) result += char;
  }
  return result;
}

/** Display form: a hyphen after the third character (`ONC-482`). */
function format(value: string): string {
  if (value.length <= PREFIX_LENGTH) return value;
  return `${value.slice(0, PREFIX_LENGTH)}-${value.slice(PREFIX_LENGTH)}`;
}

export interface CodeEntryInputProps {
  /** The unformatted code (0–6 chars from the allowed alphabet). */
  value: string;
  /** Receives the unformatted code on every change. */
  onChange: (value: string) => void;
  /** Called with the unformatted code once 6 valid chars are entered and the
   *  user commits via Enter or by blurring the field. */
  onSubmit?: (value: string) => void;
  /** Validation message (wired up by E6.4); marks the field invalid. */
  error?: string;
  /** Optional id for the underlying input; one is generated otherwise. */
  id?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Input for a 6-character handover code (`WRD-XXX`: ward prefix + random
 * suffix, per the v1 design §6.6). Hand-rolls the "drop banned chars silently"
 * masking rather than using a masked-input library, since that behaviour is
 * unusual enough to be clearer written out.
 *
 * Storybook isn't set up in this workspace; the four required states (empty,
 * partial, complete, error) are exercised instead by
 * `tests/components/code-entry-input.spec.tsx`.
 */
export function CodeEntryInput({
  value,
  onChange,
  onSubmit,
  error,
  id,
  disabled,
  autoFocus,
  className,
}: CodeEntryInputProps): React.JSX.Element {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const hasError = Boolean(error);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(sanitize(event.target.value));
  };

  const submitIfComplete = (): void => {
    if (value.length === CODE_LENGTH) onSubmit?.(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitIfComplete();
    }
  };

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Input
        id={inputId}
        value={format(value)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={submitIfComplete}
        aria-label="Handover code"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-describedby={hintId}
        aria-invalid={hasError || undefined}
        aria-errormessage={hasError ? errorId : undefined}
        className="min-h-11 text-center font-mono text-lg tracking-[0.3em]"
      />
      <span id={hintId} className="text-sm text-muted-foreground">
        {HINT}
      </span>
      {hasError ? (
        <span id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </span>
      ) : null}
    </div>
  );
}
